/**
 * COLONNES POUR TASKSPAGE
 * Définition des colonnes pour les 3 onglets
 */

import type { ColumnDef } from '@tanstack/react-table';
import { ActionsCell } from '@/components/tables/cells';
import { TableCell } from '@/components/table';
import type { FormSubmission } from '@/types/form';
import type { Crew } from '@/types/crew';

export function createProspectColumns(onView: (id: string) => void): ColumnDef<FormSubmission>[] {
  return [
    {
      accessorFn: (row) => row.form?.origin?.label || '-',
      id: 'origin',
      header: 'Origine',
      cell: ({ getValue }) => <TableCell>{getValue() as string}</TableCell>,
    },
    {
      accessorFn: (row) => row.contact?.last_name || '-',
      id: 'last_name',
      header: 'Nom',
      cell: ({ getValue }) => (
        <TableCell className="font-semibold">{getValue() as string}</TableCell>
      ),
    },
    {
      accessorFn: (row) => row.contact?.first_name || '-',
      id: 'first_name',
      header: 'Prénom',
      cell: ({ getValue }) => (
        <TableCell className="font-semibold">{getValue() as string}</TableCell>
      ),
    },
    {
      accessorFn: (row) => row.contact?.email || '-',
      id: 'email',
      header: 'Email',
      cell: ({ getValue }) => <TableCell>{getValue() as string}</TableCell>,
    },
    {
      accessorFn: (row) => row.contact?.phone || '-',
      id: 'phone',
      header: 'Téléphone',
      cell: ({ getValue }) => <TableCell>{getValue() as string}</TableCell>,
    },
    {
      accessorFn: (row) => new Date(row.created_at).toLocaleDateString('fr-FR'),
      id: 'created_at',
      header: 'Date',
      cell: ({ getValue }) => <TableCell>{getValue() as string}</TableCell>,
    },
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => <ActionsCell onView={() => onView(row.original.id)} />,
      enableSorting: false,
      size: 80,
    },
  ];
}

export function createCrewColumns(onView: (id: string) => void): ColumnDef<Crew>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Nom',
      cell: ({ getValue }) => (
        <TableCell className="font-semibold">{getValue() as string}</TableCell>
      ),
      minSize: 150,
    },
    {
      accessorKey: 'crew_number',
      header: 'N°',
      cell: ({ getValue }) => <TableCell>{(getValue() as string) || '-'}</TableCell>,
    },
    {
      accessorKey: 'status',
      header: 'Statut',
      cell: ({ getValue }) => {
        const status = getValue() as string;
        const labels: Record<string, string> = {
          bpi_payment_pending: 'BPI en attente',
          pre_registered: 'Pré-inscrit',
          registered: 'Inscrit',
          completed: 'Terminé',
        };
        return <TableCell>{labels[status] || status}</TableCell>;
      },
    },
    {
      accessorKey: 'probability',
      header: 'Probabilité',
      cell: ({ getValue }) => {
        const prob = getValue() as string;
        const labels: Record<string, string> = {
          low: 'Faible',
          medium: 'Moyenne',
          high: 'Forte',
        };
        return <TableCell>{labels[prob] || prob}</TableCell>;
      },
    },
    {
      accessorFn: (row) => row.product?.name || '-',
      id: 'product',
      header: 'Produit',
      cell: ({ getValue }) => <TableCell>{getValue() as string}</TableCell>,
    },
    {
      accessorFn: (row) => new Date(row.created_at).toLocaleDateString('fr-FR'),
      id: 'created_at',
      header: 'Date',
      cell: ({ getValue }) => <TableCell>{getValue() as string}</TableCell>,
    },
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => <ActionsCell onView={() => onView(row.original.id)} />,
      enableSorting: false,
      size: 80,
    },
  ];
}
